import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/Schedule";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import SystemDialog from "../../UIToolkit/Popups/Dialog";
import type { BulkNurseWelcomeResponse } from "./types";

type Props = {
	open: boolean;
	result: BulkNurseWelcomeResponse;
	/** The admin who pressed Send — the send is attributable, so the screen says who did it. */
	sentByFirstName: string;
	onClose: () => void;
};

/** One outcome line. Rendered only when its number is non-zero, so nothing reports a zero. */
const DoneLine: FC<{ icon: ReactNode; text: string; dataTestId: string }> = ({ icon, text, dataTestId }) => (
	<Box component="li" data-testid={dataTestId} sx={{ display: "flex", alignItems: "center", gap: 1.25, fontSize: "13.5px", color: INK.SECONDARY }}>
		<Box sx={{ display: "flex", color: INK.MUTED, "& svg": { fontSize: 17 } }}>{icon}</Box>
		{text}
	</Box>
);

/**
 * Replaces the composer once the batch has gone out. Narrower than the dialog it replaces: there is
 * nothing left to decide here, only a receipt to read.
 */
export const ResendWelcomeDone: FC<Props> = ({ open, result, sentByFirstName, onClose }) => {
	const { t } = useTranslation();
	const sentAt = DateTime.fromISO(result.sentAt);
	const expiresAt = DateTime.fromISO(result.linkExpiresAt);
	const recipientCount = result.results.filter((outcome) => outcome.sent).length;

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			maxWidth="xs"
			fullWidth
			dataTestId="resend-welcome-done-dialog"
			// A receipt has nothing to cancel: the single button just closes it.
			hideCancel
			confirm={t("team.resend_welcome.done.back_to_team")}
			confirmButtonVariant="primary"
			onConfirm={onClose}
			title={
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<Box
						sx={(theme) => ({
							width: 32,
							height: 32,
							flexShrink: 0,
							borderRadius: "50%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: alpha(theme.palette.success.main, 0.14),
							color: theme.palette.success.dark,
						})}
					>
						<CheckRoundedIcon sx={{ fontSize: 19 }} />
					</Box>
					<Box>
						<Box component="h2" sx={{ m: 0, fontSize: FONT_SIZE.TITLE_MD, fontWeight: 600, color: INK.PRIMARY }} data-testid="resend-welcome-done-title">
							{t("team.resend_welcome.done.title", { n: recipientCount })}
						</Box>
						<Box sx={{ mt: 0.375, fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
							{t("team.resend_welcome.done.when", {
								date: sentAt.toFormat("MMM d, yyyy"),
								time: sentAt.toFormat("h:mm a"),
								name: sentByFirstName,
							})}
						</Box>
					</Box>
				</Box>
			}
			body={
				<Box component="ul" sx={{ display: "flex", flexDirection: "column", gap: 1.25, m: 0, p: 0, listStyle: "none" }}>
					{result.sentBySms > 0 && (
						<DoneLine icon={<SmsOutlinedIcon />} text={t("team.resend_welcome.done.by_sms", { n: result.sentBySms })} dataTestId="resend-welcome-done-sms" />
					)}
					{result.sentByEmail > 0 && (
						<DoneLine
							icon={<EmailOutlinedIcon />}
							text={t("team.resend_welcome.done.by_email", { n: result.sentByEmail })}
							dataTestId="resend-welcome-done-email"
						/>
					)}
					{/* Always shown: the expiry is the one fact that outlives the send and bounds what the recipient can still do. */}
					<DoneLine
						icon={<ScheduleOutlinedIcon />}
						text={t("team.resend_welcome.done.links_expire", { date: expiresAt.toFormat("MMM d") })}
						dataTestId="resend-welcome-done-expiry"
					/>
				</Box>
			}
		/>
	);
};
